import React from "react";
import { useTranslation } from "react-i18next";
import Container from "react-bootstrap/Container";
import styled from "styled-components";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { SchoolStudent, TeacherSectionStudentList } from "../../common/api";
import { ApiContext } from "../../common/context/ApiContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { Account } from "../../common/context/AccountContext";
import { Checkbox, FormControlLabel, FormGroup, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleMinus } from "@fortawesome/pro-regular-svg-icons";
import {
  COLOR_GRAY_50,
  COLOR_GRAY_MUTED,
  COLOR_RED,
} from "../../common/colors";

const Subhead = styled.span`
  color: ${COLOR_GRAY_MUTED};
`;

const StudentCount = styled.span`
  font-weight: 600;
`;

const UnselectStudentButton = styled(FontAwesomeIcon)`
  cursor: pointer;
  padding-right: 0.5em;
  width: 1em;
  color: ${COLOR_RED};
`;

const StudentNameSearchInput = styled(Form.Control)`
  background-color: ${COLOR_GRAY_50};
  border: none;
  border-radius: 2rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;

  ::placeholder {
    color: ${COLOR_GRAY_MUTED};
    padding-left: 100px;
  }
`;

export const enum StudentSelectorMode {
  LESSON_ASSIGNMENT,
  CUSTOM_SECTION,
}

export const labelForStudent = (s: Readonly<SchoolStudent>) =>
  s.roster_first_name + " " + s.roster_last_name + ".";

// TODO: use React.memo to optimize rerenders?
export const StudentSelector: React.FC<{
  account: Account;
  mode: StudentSelectorMode;
  selectedStudents: ReadonlyMap<number, Readonly<SchoolStudent>>;
  setSelectedStudents: (
    selectedUsers: ReadonlyMap<number, Readonly<SchoolStudent>>
  ) => void;
  alreadyAssignedStudentIds: number[];
}> = ({
  account,
  mode,
  selectedStudents,
  setSelectedStudents,
  alreadyAssignedStudentIds,
}) => {
  const [studentNameFilter, setStudentNameFilter] = React.useState("");
  const [distinctStudents, setDistinctStudents] = React.useState<
    Readonly<SchoolStudent>[]
  >([]);
  const [
    distinctAlreadyAssignedStudentIds,
    setDistinctAlreadyAssignedStudentIds,
  ] = React.useState<Set<number>>(new Set());
  const [visibleStudents, setVisibleStudents] = React.useState<
    Readonly<SchoolStudent>[]
  >([]);
  const [selectedSections, setSelectedSections] = React.useState<Set<number>>(
    new Set()
  );
  const { t } = useTranslation();
  const { getTeacherSections } = React.useContext(ApiContext);
  const [sections, setSections] = React.useState<TeacherSectionStudentList[]>(
    []
  );
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const dataFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);

  React.useEffect(() => {
    (async () => {
      if (
        account.userCurrentVersion !== dataFetchedForUserVersion.current &&
        account.userCurrentVersion !== fetchJobForUserVersion.current
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        fetchJobForUserVersion.current = account.userCurrentVersion;
        const { sections } = await getTeacherSections();
        if (expectedVersion !== fetchJobForUserVersion.current) {
          return;
        }
        setSections(sections);
        dataFetchedForUserVersion.current = fetchJobForUserVersion.current;
        stopLoading();
      }
    })();
  }, [account, getTeacherSections, startLoading, stopLoading]);

  // Calculate distinct list of students whenever we sections data from API
  // and sort it by first name, last name. Needs to be done once per API call.
  React.useEffect(() => {
    if (!sections) {
      return;
    }
    const students = new Map<number, Readonly<SchoolStudent>>();
    sections.forEach((section) => {
      section.student_list.forEach((student) =>
        students.set(student.id, student)
      );
    });
    const sorted = Array.from(students.values()).sort((a, b) =>
      labelForStudent(a).localeCompare(labelForStudent(b))
    );
    setDistinctStudents(sorted);
    setDistinctAlreadyAssignedStudentIds(
      new Set(alreadyAssignedStudentIds.filter((id) => students.has(id)))
    );
  }, [
    sections,
    setDistinctStudents,
    alreadyAssignedStudentIds,
    setDistinctAlreadyAssignedStudentIds,
  ]);

  // Calculate visible list of students based on filter.
  // Must be done every time we reload sections from API or name filter changes.
  React.useEffect(() => {
    if (studentNameFilter) {
      const lowerCaseFilter = studentNameFilter.toLocaleLowerCase();
      const filtered = distinctStudents.filter((student) =>
        student.roster_first_name.toLocaleLowerCase().includes(lowerCaseFilter)
      );
      setVisibleStudents(filtered);
    } else {
      setVisibleStudents(distinctStudents);
    }
  }, [studentNameFilter, distinctStudents, setVisibleStudents]);

  const onChangeStudentCheckbox = React.useCallback(
    (changedStudent: Readonly<SchoolStudent>, isChecked: boolean) => {
      const newSelectedStudents = new Map(selectedStudents);
      if (isChecked) {
        newSelectedStudents.set(changedStudent.id, changedStudent);
      } else {
        const newSelectedSections = new Set(selectedSections);
        newSelectedStudents.delete(changedStudent.id);
        // Deselect sections that include this student
        sections
          .filter(
            (section) =>
              section.student_list.filter(
                (student) => student.id === changedStudent.id
              ).length > 0
          )
          .forEach((section) => newSelectedSections.delete(section.id));
        if (newSelectedSections.size !== selectedSections.size) {
          setSelectedSections(newSelectedSections);
        }
      }
      setSelectedStudents(newSelectedStudents);
    },
    [
      selectedStudents,
      setSelectedStudents,
      sections,
      selectedSections,
      setSelectedSections,
    ]
  );

  const onChangeAllStudentsCheckbox = React.useCallback(
    (isChecked: boolean) => {
      // Automatically clear section selections (?)
      setSelectedSections(new Set());

      if (isChecked) {
        setSelectedStudents(
          new Map(
            distinctStudents
              .filter(
                (student) => !distinctAlreadyAssignedStudentIds.has(student.id)
              )
              .map((student) => [student.id, student])
          )
        );
      } else {
        setSelectedStudents(new Map());
      }
    },
    [
      distinctStudents,
      distinctAlreadyAssignedStudentIds,
      setSelectedStudents,
      setSelectedSections,
    ]
  );

  const onChangeSectionCheckbox = React.useCallback(
    (
      changedSection: Readonly<TeacherSectionStudentList>,
      isChecked: boolean
    ) => {
      const newSelectedStudents = new Map(selectedStudents);
      const newSelectedSections = new Set(selectedSections);

      // Main rule: selected students set must include all students included in the selected sections
      if (isChecked) {
        // Adding a section: just ensure that each student is selected.
        // (If superset section gets selected we select all individual student,
        // but don't automatically mark subset sections as selected.)
        newSelectedSections.add(changedSection.id);
        changedSection.student_list
          .filter(
            (student) => !distinctAlreadyAssignedStudentIds.has(student.id)
          )
          .forEach((student) => newSelectedStudents.set(student.id, student));
      } else {
        // Removing a section is done in 2 steps:
        // 1. Unselect students of this section
        newSelectedSections.delete(changedSection.id);
        changedSection.student_list.forEach((student) =>
          newSelectedStudents.delete(student.id)
        );

        // 2. Reselect students included in other selected sections
        sections.forEach((section) => {
          if (newSelectedSections.has(section.id)) {
            section.student_list
              .filter(
                (student) => !distinctAlreadyAssignedStudentIds.has(student.id)
              )
              .forEach((student) =>
                newSelectedStudents.set(student.id, student)
              );
          }
        });
      }
      setSelectedSections(newSelectedSections);
      setSelectedStudents(newSelectedStudents);
    },
    [
      sections,
      selectedSections,
      distinctAlreadyAssignedStudentIds,
      setSelectedSections,
      selectedStudents,
      setSelectedStudents,
    ]
  );

  if (!distinctStudents) {
    return <></>;
  }

  return (
    <Container className="my-4" style={{ maxWidth: "800px" }}>
      {/* Filters and student list are laid out differently for diff widths:
          - xs, sm and md: 1 column, filters above list
          - lg+: 2 columns, filters then list
          Subhead appears above filters always. */}
      <Row>
        {/* Filters */}
        <Col
          xs={{ span: 8, offset: 2 }}
          lg={{ span: 6, offset: 0 }}
          className="h-100"
        >
          <Row className="justify-content-center mb-3">
            <Col xs="auto">
              <Subhead>
                {mode === StudentSelectorMode.LESSON_ASSIGNMENT
                  ? t("assignments_student_selector")
                  : t("create_instructional_group_student_selector")}
              </Subhead>
            </Col>
          </Row>
          <Row>
            {/* "Search student by name" input field */}
            <InputGroup className="mb-3">
              <StudentNameSearchInput
                className="ps-4"
                placeholder={t("assignments_search_by_student_name")}
                aria-label={t("assignments_search_by_student_name")}
                value={studentNameFilter}
                onChange={(e) => setStudentNameFilter(e.target.value)}
              />
            </InputGroup>
          </Row>
          <Row className="mt-2 ps-2">
            {/* "All students" filter checkbox */}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    style={{ marginLeft: "0px" }}
                    checked={
                      selectedStudents.size > 0 &&
                      selectedStudents.size +
                        distinctAlreadyAssignedStudentIds.size ===
                        distinctStudents.length
                    }
                    onChange={(event) =>
                      onChangeAllStudentsCheckbox(event.target.checked)
                    }
                  />
                }
                label={
                  t("assignments_all_students") +
                  ` (${distinctStudents.length})`
                }
              ></FormControlLabel>
            </FormGroup>
          </Row>
          <Row className="border-bottom my-3"></Row>
          {/* Separate filter checkbox for each section */}
          {sections.map((section) => (
            <Row key={section.id} className="ps-2">
              <FormControlLabel
                control={
                  <Checkbox
                    style={{ marginLeft: "0px" }}
                    checked={selectedSections.has(section.id)}
                    onChange={(event) =>
                      onChangeSectionCheckbox(section, event.target.checked)
                    }
                  />
                }
                label={
                  t("assignments_all_students_in") +
                  " " +
                  section.name +
                  ` (${section.student_list.length})`
                }
              ></FormControlLabel>
            </Row>
          ))}
          <Row className="border-bottom my-3"></Row>
          <FormGroup>
            {visibleStudents.map((student) => {
              return (
                <Row key={student.id} className="ps-2">
                  <Tooltip
                    title={
                      distinctAlreadyAssignedStudentIds.has(student.id)
                        ? t("assignments_student_already_assigned")
                        : ""
                    }
                    placement="top-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedStudents.has(student.id)}
                          onChange={(event) =>
                            onChangeStudentCheckbox(
                              student,
                              event.target.checked
                            )
                          }
                        />
                      }
                      label={
                        <span
                          style={
                            distinctAlreadyAssignedStudentIds.has(student.id)
                              ? {
                                  textDecoration: "underline",
                                  textDecorationStyle: "dashed",
                                }
                              : {}
                          }
                        >
                          {labelForStudent(student)}
                        </span>
                      }
                    ></FormControlLabel>
                  </Tooltip>
                </Row>
              );
            })}
          </FormGroup>
        </Col>
        {/* Vertical bar for lg+ as tall as the first column */}
        <Col className="d-none d-lg-flex justify-content-center" xs={0} lg={1}>
          <div className="vr h-100"></div>
        </Col>
        {/* Second column visible only in lg+ */}
        <Col className="d-none d-lg-inline" xs={0} lg={5}>
          <Row className="justify-content-center mb-3">
            <Col xs="auto">
              <StudentCount>{`${selectedStudents.size} `}</StudentCount>
              <span>
                {t("assignments_students_selected", {
                  count: selectedStudents.size,
                })}
              </span>
            </Col>
          </Row>
          {distinctStudents
            .filter((s) => selectedStudents.has(s.id))
            .map((student) => (
              <Row key={student.id} className="my-2">
                <Col>
                  <UnselectStudentButton
                    icon={faCircleMinus}
                    onClick={() => onChangeStudentCheckbox(student, false)}
                  />
                  <span style={{ alignContent: "center" }}>
                    {student.roster_first_name}
                  </span>
                </Col>
              </Row>
            ))}
        </Col>
      </Row>
    </Container>
  );
};
